import React from 'react';
import { Thermometer, Droplets, Zap, Sun, Users } from 'lucide-react';

export interface SensorDisplayStyle06Props {
  type?: 'temperature' | 'humidity' | 'power' | 'lux' | 'occupancy';
  value?: number | boolean;
  label?: string;
  unit?: string;
  size?: 'small' | 'medium' | 'large';
}

const SENSOR_CONFIG: Record<string, {
  icon: React.ElementType;
  color: string;
  unit: string;
  label: string;
  formatValue: (v: any) => string;
}> = {
  temperature: {
    icon: Thermometer,
    color: 'text-orange-400',
    unit: '°C',
    label: '室內溫度',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(1) : '--'
  },
  humidity: {
    icon: Droplets,
    color: 'text-blue-400',
    unit: '%',
    label: '室內濕度',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(0) : '--'
  },
  power: {
    icon: Zap,
    color: 'text-yellow-400',
    unit: 'W',
    label: '功率',
    formatValue: (v) => {
      if (typeof v !== 'number') return '--';
      return v >= 1000 ? `${(v / 1000).toFixed(2)}kW` : `${v.toFixed(0)}W`;
    }
  },
  voltage: {
    icon: Zap,
    color: 'text-yellow-300',
    unit: 'V',
    label: '電壓',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(1) : '--'
  },
  current: {
    icon: Zap,
    color: 'text-orange-300',
    unit: 'A',
    label: '電流',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(2) : '--'
  },
  energy: {
    icon: Zap,
    color: 'text-green-400',
    unit: 'kWh',
    label: '累積用電',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(2) : '--'
  },
  lux: {
    icon: Sun,
    color: 'text-amber-400',
    unit: 'lux',
    label: '光照',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(0) : '--'
  },
  occupancy: {
    icon: Users,
    color: 'text-purple-400',
    unit: '',
    label: '佔用狀態',
    formatValue: (v) => v ? '有人' : '無人'
  }
};

/**
 * SensorDisplay Style 06 - CardAlt
 * Horizontal card with icon on left and value on right
 * Uses type parameter to determine icon and color
 */
export const Style06_Card: React.FC<SensorDisplayStyle06Props> = ({
  type = 'temperature',
  value,
  label,
  unit,
  size = 'medium'
}) => {
  const config = SENSOR_CONFIG[type] || SENSOR_CONFIG.temperature;
  const Icon = config.icon;
  const displayLabel = label ?? config.label;
  const displayUnit = unit ?? config.unit;
  const displayValue = config.formatValue(value);
  const valueWithUnit = type === 'power' ? displayValue : `${displayValue}${displayUnit}`;

  return (
    <div className="flex items-center justify-between px-4 py-3 bg-slate-800/30 rounded-xl border border-slate-700/30">
      <div className="flex items-center gap-2">
        <Icon className={`w-5 h-5 ${config.color}`} />
        <span className="text-sm text-slate-300">{displayLabel}</span>
      </div>
      <span className={`text-lg font-bold ${config.color}`}>
        {valueWithUnit}
      </span>
    </div>
  );
};

export default Style06_Card;
